import {
  GamoraControllerStateArgs,
  GamoraControllerStateMap,
} from '@ver.id/libs.internal.gamora';
import { HoruzError } from '../../../../errors/error.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakServiceStateContext } from '../context.js';
import { HoruzProtocolChulakStateBase } from '../base.js';
import { IHoruz } from '../../../../horuz.js';

/**
 * HoruzProtocolChulakServiceState
 *
 * Abstract class that represents the state node
 * @internal
 */
export class HoruzProtocolChulakServiceStateBase<
  TStateMap extends GamoraControllerStateMap,
  TStateContext extends HoruzProtocolChulakServiceStateContext,
  TRoot extends IHoruz,
> extends HoruzProtocolChulakStateBase<TStateMap, TStateContext, TRoot> {
  constructor(args: GamoraControllerStateArgs) {
    super(args);
  }

  /**
   * Gets store
   */
  get serviceSessionStore() {
    return this.$root.$('protocols').$('chulak').$('store').$('serviceSession');
  }

  /**
   * Try to handle exception
   * @param stateContext
   */
  protected _tryHandleException(
    serviceStateContext: TStateContext,
    e?: unknown,
  ) {
    if (
      this.$isInCurrentState(serviceStateContext) &&
      !this.$hasPostedTransition(serviceStateContext)
    ) {
      this._trySetLastError(serviceStateContext, e);
      this.$postTransition(serviceStateContext, { stateNext: 'Abort' });
    }
  }

  /**
   * Try to set last error
   * @param stateContext
   */
  protected _trySetLastError = (
    serviceStateContext: TStateContext,
    e?: unknown,
  ) => {
    if (e instanceof HoruzError) {
      serviceStateContext.stateContextRegistry.lastError = e;
    }
  };

  /**
   * Try to post task to send a message and transition to the next state
   * @param serviceStateContext The service state context
   * @param stateNext The name of the next state
   * @param message The message to send
   */
  protected _trySendMessageAndTransition(
    serviceStateContext: TStateContext,
    stateNext: keyof TStateMap,
    message: Uint8Array,
  ) {
    this.$tryPostTask(serviceStateContext, async () => {
      if (this.$hasPostedTransition(serviceStateContext)) {
        // Already posted transition, meaning that the code below has already
        // been executed for this specific WebSocket connection in the current
        // state.
        return;
      }

      // Send start message
      await this.send(
        serviceStateContext,
        HoruzProtocolChulakMessageModule.Decode(message),
      );

      // Transition this context to next
      this.$tryPostTransition(serviceStateContext, {
        stateNext: stateNext,
      });
    });
  }
}
